import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatUsd } from "@veyso/core";
import { ErrorNote } from "../components/ui.js";
import type { ChatTurn } from "./studio.js";

/** Why asking for a version was refused, sorted by what to do about it. */
export interface PostError {
  kind: "credit" | "running" | "other";
  message: string;
  /** Refused while trying a failed first film again, which is the launch video itself. */
  firstFilm?: boolean;
}

/** A refusal, with the one useful next step where there is one. */
export const StudioError: React.FC<{ error: PostError }> = ({ error }) => (
  <ErrorNote>
    {error.kind === "credit" ? (
      <>
        {error.firstFilm ? "Add credit to make your launch video." : "Add credit to make another version."}{" "}
        <Link to="/settings#billing" className="font-semibold underline">
          Add credit
        </Link>
      </>
    ) : error.kind === "running" ? (
      "A version is already being made."
    ) : (
      error.message
    )}
  </ErrorNote>
);

const price = (amount: number) => (amount > 0 ? formatUsd(amount) : "Free");

/**
 * Asking for a change in words.
 *
 * The history is the versions themselves, so it survives a reload and never
 * disagrees with the strip: each request, then what changed and what it cost.
 */
export const RevisionChat: React.FC<{
  turns: ChatTurn[];
  quoteUsd: number;
  balanceUsd: number | null;
  busy: boolean;
  /** Why nothing can be asked for right now; null when it can. */
  disabledReason: string | null;
  /** The version being made, shown under the request that asked for it. */
  makingNumber: number | null;
  error: PostError | null;
  onSend: (message: string) => Promise<boolean>;
}> = ({ turns, quoteUsd, balanceUsd, busy, disabledReason, makingNumber, error, onSend }) => {
  const [message, setMessage] = useState("");
  const log = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const blocked = busy || disabledReason !== null;

  // The newest turn is the one that matters; keep it in view as turns arrive.
  useEffect(() => {
    const element = log.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [turns.length, makingNumber, error]);

  const send = async () => {
    const text = message.trim();
    if (!text || blocked) return;
    if (await onSend(text)) setMessage("");
  };

  const tryAgain = (text: string) => {
    setMessage(text);
    input.current?.focus();
  };

  return (
    <aside className="flex h-[640px] min-w-0 flex-col overflow-hidden rounded-[14px] border border-[var(--edge)] bg-bay-850 lg:h-[760px]">
      <div className="flex flex-col gap-0.5 border-b border-[var(--edge)] px-4 py-3.5">
        <h2 className="text-[14px] font-semibold text-ink-100">Ask for a change</h2>
        <span className="text-[12px] text-ink-300">
          Each change makes a new version of the voice-over and the motion graphic.
        </span>
      </div>

      <div
        ref={log}
        role="log"
        aria-live="polite"
        aria-label="Changes asked for"
        className="flex flex-1 flex-col gap-3 overflow-y-auto p-4"
      >
        <Bubble side="veyso">
          Tell me what to change — the script, the order, the pace, the colours or the style — and I will make a new
          version.
        </Bubble>

        {turns.map((turn, index) => (
          <div
            key={`${turn.version}-${turn.role}-${index}`}
            className={`flex flex-col gap-1.5 ${
              turn.role === "event" ? "items-center" : turn.role === "customer" ? "items-end" : "items-start"
            } ${turn.removed ? "opacity-55" : ""}`}
          >
            {turn.role === "event" ? (
              <div className="flex items-center gap-2 rounded-full bg-bay-800 px-2.5 py-1.5 text-[11.5px] text-ink-300">
                <span className="font-semibold text-ink-100">v{turn.version}</span>
                <span>
                  {turn.text}
                  {turn.priceUsd !== null ? ` · ${price(turn.priceUsd)}` : ""}
                  {turn.removed ? " (removed)" : ""}
                </span>
              </div>
            ) : (
              <Bubble side={turn.role}>{turn.text}</Bubble>
            )}

            {turn.role === "veyso" ? (
              <div className="tnum flex items-center gap-2 pl-0.5 text-[11.5px] text-ink-300">
                <span className="font-semibold text-ink-100">v{turn.version}</span>
                <span>
                  {turn.priceUsd && turn.priceUsd > 0 ? `Charged ${formatUsd(turn.priceUsd)}` : "Free"}
                  {turn.removed ? " (removed)" : ""}
                </span>
              </div>
            ) : null}

            {turn.role === "event" && turn.failed ? (
              // Nothing to put back in the box: a script edit stays in the
              // script panel, and a first film is tried again from the player.
              <span className="text-[11.5px] text-ink-300">Couldn't be made — nothing was charged.</span>
            ) : null}

            {turn.role === "customer" && turn.failed ? (
              <div className="flex items-center gap-2 pr-0.5 text-[11.5px] text-ink-300">
                <span>Couldn't be made — nothing was charged.</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={busy}
                  onClick={() => tryAgain(turn.text)}
                >
                  Try again
                </button>
              </div>
            ) : null}
            {turn.failed && turn.error ? (
              // What went wrong, in the job's own short words, under the line
              // saying nothing was charged.
              <span className="max-w-[90%] pr-0.5 text-[11px] leading-[1.45] text-ink-400">{turn.error}</span>
            ) : null}

            {turn.role === "customer" && turn.version === makingNumber ? (
              <span className="pr-0.5 text-[11.5px] text-ink-300">Making v{turn.version}…</span>
            ) : null}
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2 border-t border-[var(--edge)] px-3.5 py-3">
        {error ? <StudioError error={error} /> : null}
        <label htmlFor="launch-change" className="sr-only">
          Describe a change
        </label>
        <div className="flex items-end gap-2">
          <textarea
            id="launch-change"
            ref={input}
            rows={2}
            className="input min-h-[44px] flex-1 resize-none text-[13px] leading-[1.5]"
            placeholder="Describe a change — e.g. make the opening punchier, use darker colours, slow the pace"
            value={message}
            disabled={busy}
            onChange={(event) => setMessage(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                void send();
              }
            }}
          />
          <button
            type="button"
            className="btn btn-primary h-[44px]"
            disabled={blocked || message.trim().length === 0}
            onClick={() => void send()}
          >
            Send
          </button>
        </div>
        <span className="tnum text-[11.5px] text-ink-300">
          {disabledReason ??
            // Nothing quoted: the team is free forever.
            (quoteUsd <= 0
              ? "New versions are free."
              : balanceUsd === null
                ? `A new version costs ${formatUsd(quoteUsd)}.`
                : `A new version costs ${formatUsd(quoteUsd)} from your balance (${formatUsd(balanceUsd)} left).`)}
        </span>
      </div>
    </aside>
  );
};

const Bubble: React.FC<{ side: "customer" | "veyso"; children: React.ReactNode }> = ({ side, children }) => (
  <div
    className={`whitespace-pre-wrap px-3 py-2.5 text-[13px] leading-[1.5] ${
      side === "customer"
        ? "max-w-[85%] self-end rounded-[12px_12px_4px_12px] bg-ink-100 text-bay-850"
        : "max-w-[90%] self-start rounded-[12px_12px_12px_4px] bg-bay-800 text-ink-100"
    }`}
  >
    {children}
  </div>
);
